// Super-property values for PostHog `environment`.
// Staging deploys are Vite/Cloud production builds, so MODE/NODE_ENV must
// not win over the Firebase/GCP project id.

export type AnalyticsEnvironment = 'production' | 'staging' | 'development'

export interface AnalyticsEnvironmentInput {
  explicit?: string | null
  projectIds?: Array<string | null | undefined>
  mode?: string | null
  emulator?: boolean
}

const ALLOWED = new Set<AnalyticsEnvironment>(['production', 'staging', 'development'])

function normalize(value?: string | null): AnalyticsEnvironment | null {
  if (!value) return null
  const v = value.trim().toLowerCase()
  if (v === 'prod') return 'production'
  if (v === 'dev' || v === 'local' || v === 'test') return 'development'
  if (ALLOWED.has(v as AnalyticsEnvironment)) return v as AnalyticsEnvironment
  return null
}

function haystack(projectIds?: Array<string | null | undefined>): string {
  return (projectIds ?? [])
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
    .join(' ')
    .toLowerCase()
}

export function resolveAnalyticsEnvironment(input: AnalyticsEnvironmentInput): AnalyticsEnvironment {
  if (input.emulator) {
    return 'development'
  }

  const projects = haystack(input.projectIds)

  // Known staging project must never classify as production.
  if (projects.includes('staging')) {
    return 'staging'
  }
  if (projects.includes('production')) {
    return 'production'
  }

  const explicit = normalize(input.explicit)
  if (explicit) {
    return explicit
  }

  if (projects.includes('mysensorybook-dev') || /(^|[^a-z])dev([^a-z]|$)/.test(projects)) {
    return 'development'
  }

  const mode = normalize(input.mode)
  if (mode) {
    return mode
  }

  return 'development'
}

export function resolveFrontendAnalyticsEnvironment(): AnalyticsEnvironment {
  return resolveAnalyticsEnvironment({
    explicit: import.meta.env.VITE_APP_ENVIRONMENT as string | undefined,
    projectIds: [import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined],
    mode: import.meta.env.MODE,
    emulator: import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true',
  })
}
